/**
 * @desc Client HTTP API.
 */
import { each, isEmpty, remove, extend, defaults, assign, concat } from 'lodash-es'
import axios from 'axios'
import querystring from 'querystring'
import auth from '@/api/auth'
import router from '@/router'
import constants from '@/configuration/constants'
import eventBus from '@/modules/eventBus'

/**
* Pool of async requests to be able to cancel them
* when navigating to another screen
*/
const asyncReqPool = {
  // All requests
  requests: [],
  // Cancel requests and clear timers
  clear: () => {
    each(asyncReqPool.requests, promise => {
      promise.cancel()
    })
  }
}

/**
 * @desc Execute a request.
 * @param {String}  method  - Method.
 * @param {String}  url     - URL or path.
 * @param {Object}  data    - Data request or body.
 * @param {Object}  headers - Request headers.
 * @param {Object}  options - Other options.
 */
const request = (method, url, data, headers, options = {}) => {
  // Get current route, the one which launched the request
  // Needed to prevent to display result/message if route change before Promise is resolved/rejected
  const origineRoute = router.currentRoute

  const isAuthRoute = origineRoute.matched.some(route => route.meta.requiresAuth)
  if (isAuthRoute && !auth.isAuthenticated()) {
    auth.logout()
    return Promise.reject(new Error('User not connected'))
  }

  eventBus.$emit('request-start', method, url, data, headers, options)

  method = method.toLowerCase()
  let config = {
    method,
    url,
    data
  }

  // Add GMS content type header
  // If needed, use headers param to override it
  config.headers = {}
  config.headers['Content-Type'] = 'application/json;charset=UTF-8'

  if (!isEmpty(headers)) {
    for (const headerKey in headers) {
      config.headers[headerKey] = headers[headerKey]
    }
  }

  let sort = null
  if (method === 'get' && !isEmpty(data)) {
    // Add data as "GET" request parameters
    config.url += (/\?/.test(url) ? '&' : '?') + querystring.stringify(data)
    // Needed for content type header
    config.data = {}
    // Save sort in order to reinject it into the paginatedDto response
    sort = data.sort
  }

  if (config.headers['Content-Type'].indexOf('application/x-www-form-urlencoded') !== -1 && !isEmpty(data)) {
    config.data = querystring.stringify(data)
  }

  // Set responseType if lastPoll for downloading files
  if (options.lastPoll && !isEmpty(options.responseType)) {
    // Needed to download binary files
    config.responseType = options.responseType
  }
  config.options = options

  // Optional timeout configuration
  config.timeout = options.timeout || constants.requestTimeout

  return axios(config)
    .then(response => {
      // Return result only if the currentRoute is the origineRoute
      if (router.currentRoute.path === origineRoute.path || router.currentRoute.meta.checkFleets || router.currentRoute.meta.checkClusters) {
        // Inject sort into the paginatedDto response if it is set
        if (sort) {
          response.data.sort = sort
        }

        eventBus.$emit('request-succeed', response)

        return response
      }
    })
    .catch(error => {
      // Return error only if the currentRoute is the origineRoute
      if (router.currentRoute.path === origineRoute.path) {
        eventBus.$emit('request-failed', error, config)

        return Promise.reject(error)
      }
    })
}

/**
 * @desc
 *  Execute a request with asynchronous management.<br/>
 *  The response of the request must be a task with an attribute status=["PENDING", "OK", "KO"].<br/>
 *  The response is polling.<br/>
 *  Use the cancel() method of the returned promise to stop polling. It takes the following arguments:<br/>
 *    {String}  reason - reason of the cancel action.<br/>
 *    {Boolean} silent - true to not reject the promise, false otherwise (default false).<br/>
 *                       The promise is rejected with the following object (no interceptor are called):</br>
 *                         {isCancel: true, reason: <reason>, silent: [true|false]}.
 * @param {String}  method  - Method.
 * @param {String}  url     - URL or path.
 * @param {Object}  data    - Data request or body.
 * @param {Object}  headers - Request headers.
 * @param {Object}  options - Other options.
 */
const requestAsync = (method, url, data, headers, options) => {
  // Get current route, the one which launched the request
  // Needed to prevent to display result/message if route change before Promise is resolved/rejected
  const origineRoute = router.currentRoute

  const isAuthRoute = origineRoute.matched.some(route => route.meta.requiresAuth)
  if (isAuthRoute && !auth.isAuthenticated()) {
    auth.logout()
    return Promise.reject(new Error('User not connected'))
  }

  eventBus.$emit('request-async-start', method, url, data, headers, options)

  // timeout promise used to stop polling when the timeout is reached
  let timeout = () => {}
  let promiseTimeout = new Promise(resolve => {
    // Get command of the task
    let command
    const splitUrl = url.split('commands/')
    if (splitUrl.length === 2) {
      command = splitUrl[1].split('?')[0]
    }

    timeout = () => resolve({
      isTimeout: true,
      command
    })
  })
  setTimeout(() => {
    timeout()
  }, constants.pollTimeoutMillis)

  // cancel promise use to manually stop polling
  let cancel
  let promiseCancel = new Promise(resolve => {
    cancel = (reason = '', silent = false) => resolve({
      isCancel: true,
      reason: reason,
      silent: silent
    })
  })

  // create the task
  const promise = request(method, url, data, headers, options).then(response => {
    return poll(response, promiseCancel, promiseTimeout, options)
  })

  // attach the cancel method to the returned promise
  promise.cancel = cancel

  // Add to async requests pool
  asyncReqPool.requests.push(promise)

  promise
    .then(response => {
      // Remove from async requests pool
      remove(asyncReqPool.requests, asyncPromise => asyncPromise === promise)

      // Return result only if the currentRoute is the origineRoute
      if (router.currentRoute.path === origineRoute.path) {
        eventBus.$emit('request-async-succeed', response)
        return response
      }
    })
    .catch(error => {
      // Remove from async requests pool
      remove(asyncReqPool.requests, asyncPromise => asyncPromise === promise)

      // Return error only if the currentRoute is the origineRoute
      if (router.currentRoute.path === origineRoute.path) {
        eventBus.$emit('request-async-failed', error, options)
      }
    })

  return promise
}

/**
 * @desc
 *  Poll a task with GET method until it switches to OK or another status.<br/>
 *  While the status is pending, poll the task at the URL containing into the response header "Content-Location".<br/>
 *  When the status is KO, the returned promise is rejected, the returned error corresponds to the task (no interceptor are called).<br/>
 *  When the status is OK, call the URL containing into the response header "Location" and return the promise of this request.
 *  29/06/18: Since WMC 3.0, the OK state is removed: the result is directly returned.
 *  11/09/19: BUGFIX WMC-2733, the OK state is required as specific options like "responseType" is usefull to correctly parse the result reponse.
 *            There is no location header anymore, just request the same URL with result = true.
 */
const poll = (response, promiseCancel, promiseTimeout, options = {}, url, iStep = 0) => {
  if (!response) return

  switch (response.data.status) {
    case 'PENDING':
      url = url || constants.gmsUrl + response.headers['content-location'] + '?result=false'
      // incremental time polling
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(
          () => request('get', url, {}, {}, options).then(resolve),
          iStep * constants.pollIntervalMillis
        )
        promiseTimeout.then(options => {
          clearTimeout(timeout)
          // If cancelled > silent
          if (!options.silent && !options.isCancel) {
            reject(options)
          }
        })
        promiseCancel.then(options => {
          clearTimeout(timeout)
          // If cancelled > silent
          if (!options.silent && !options.isCancel) {
            reject(options)
          }
        })
      }).then(response => {
        return poll(response, promiseCancel, promiseTimeout, options, url, iStep + 1)
      })
    case 'KO':
      return Promise.reject(response)
    case 'OK':
      options.lastPoll = true
      return request('get', url.replace('result=false', 'result=true'), null, null, options)
    default:
      return Promise.resolve(response)
  }
}

export default extend({}, axios, {
  /**
   * @desc Set client HTTP base URL.
   * @param {String}  gmsUrl - Base URL.
   */
  setBaseURL (gmsUrl) {
    axios.defaults.baseURL = gmsUrl
  },

  /**
   * @desc Get client HTTP base URL.
   */
  getBaseURL () {
    return axios.defaults.baseURL
  },

  /**
   * @desc Set client global HTTP header.
   * @param {String}  header  - Header name.
   * @param {Mixed}   value   - Header value.
   */
  setHeader (header, value) {
    if (value === null) {
      delete axios.defaults.headers.common[header]
    } else {
      axios.defaults.headers.common[header] = value
    }
    return this
  },

  /**
   * @desc Execute GET request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  get (url, data, headers, options) {
    return request('get', url, data, headers, options)
  },

  /**
   * @desc Execute GET request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  getAsync (url, data, headers, options) {
    return requestAsync('get', url, data, headers, options)
  },

  /**
   * @desc
   * Execute GET request and try to get all the data by looping through the pages.<br/>
   * Based on the DTO and GET parameters page, nbPages, pageSize, list and count.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  getAll (url, data, headers, options) {
    return this.get(url, defaults({ page: 1 }, data, { pageSize: constants.maxPageSizePerRequest }), headers, options)
      .then(response => {
        // Resolve the first page
        const promises = [Promise.resolve(response)]

        // Get the next other pages
        for (let page = response.data.page + 1; page <= response.data.nbPages; page++) {
          promises.push(this.get(url, assign({ pageSize: constants.maxPageSizePerRequest }, data, { page: page }), headers, options))
        }

        // Request all pages
        return axios.all(promises)
          .then(responses => {
            // Concatenate all the responses to one response
            let _response = {}
            each(responses, response => {
              if (isEmpty(_response)) {
                _response = response
              } else {
                _response.data.list = concat(_response.data.list, response.data.list)
                _response.data.count = _response.data.count + response.data.count
              }
            })
            _response.data.page = 1
            _response.data.nbPages = 1

            return _response
          })
      })
  },

  /**
   * @desc Execute HEAD request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  head (url, data, headers, options) {
    return request('head', url, data, headers, options)
  },

  /**
   * @desc Execute HEAD request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  headAsync (url, data, headers, options) {
    return requestAsync('head', url, data, headers, options)
  },

  /**
   * @desc Execute POST request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data body.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  post (url, data, headers, options) {
    return request('post', url, data, headers, options)
  },

  /**
   * @desc Execute POST request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  postAsync (url, data, headers, options) {
    return requestAsync('post', url, data, headers, options)
  },

  /**
   * @desc Execute PUT request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data body.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  put (url, data, headers, options) {
    return request('put', url, data, headers, options)
  },

  /**
   * @desc Execute PUT request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  putAsync (url, data, headers, options) {
    return requestAsync('put', url, data, headers, options)
  },

  /**
   * @desc Execute PATCH request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data body.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  patch (url, data, headers, options) {
    return request('patch', url, data, headers, options)
  },

  /**
   * @desc Execute PATCH request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  patchAsync (url, data, headers, options) {
    return requestAsync('patch', url, data, headers, options)
  },

  /**
   * @desc Execute DELETE request.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  delete (url, data, headers, options) {
    return request('delete', url, data, headers, options)
  },

  /**
   * @desc Execute DELETE request with asynchronous management.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  deleteAsync (url, data, headers, options) {
    return requestAsync('delete', url, data, headers, options)
  },

  /**
   * @desc Execute a request.
   * @param {String}  method  - Method.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request or body.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  request (method, url, data, headers, options) {
    return request(method, url, data, headers, options)
  },

  /**
   * @desc Execute a request with asynchronous management.
   * @param {String}  method  - Method.
   * @param {String}  url     - URL or path.
   * @param {Object}  data    - Data request or body.
   * @param {Object}  headers - Request headers.
   * @param {Object}  options - Other options.
   */
  requestAsync (method, url, data, headers, options) {
    return requestAsync(method, url, data, headers, options)
  },

  /**
   * @desc Clear all ongoing async reqquests.
   */
  clearAsync () {
    asyncReqPool.clear()
  },

  /**
   * @desc Add an interceptor on requests.
   * @param {Function}  beforeRequest  - Before request sent callback.
   */
  addRequestInterceptor (beforeRequest) {
    return axios.interceptors.request.use(function (config) {
      if (beforeRequest) {
        beforeRequest(config)
      }
      return config
    })
  },

  /**
   * @desc Remove an interceptor on requests.
   * @param {Object}  requestInterceptor - The request interceptor.
   */
  removeRequestInterceptor (requestInterceptor) {
    axios.interceptors.request.eject(requestInterceptor)
  },

  /**
   * @desc Add an interceptor on responses.
   * @param {Function}  responseSuccess - Callback on response success.
   * @param {Function}  requestError    - Callback on response error.
   */
  addResponseInterceptor (responseSuccess, responseError) {
    return axios.interceptors.response.use(function (config) {
      if (responseSuccess) {
        responseSuccess(config)
      }
      return config
    }, function (error) {
      if (responseError) {
        responseError(error)
      }
      return Promise.reject(error)
    })
  },

  /**
   * @desc Remove an interceptor on responses.
   * @param {Object}  responseInterceptor - The response interceptor.
   */
  removeResponseInterceptor (responseInterceptor) {
    axios.interceptors.response.eject(responseInterceptor)
  },

  /**
   * @desc Encode parameters when they need to be inserted in url.
   * @param {Object}  sourceParam - The parameters to encode. Must not be a nested object.
   */
  encodeParams (source) {
    const array = []
    for (const key in source) {
      array.push(encodeURIComponent(key) + '=' + encodeURIComponent(source[key]))
    }
    return array.join('&')
  }
})
